// src/pages/DoNotSell.jsx
import { useEffect, useMemo, useState } from "react";

export default function DoNotSell() {
  const effectiveDate = "August 23, 2025";
  const lastUpdated = "August 23, 2025";
  const [optOut, setOptOut] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const gpc = useMemo(() => {
    try {
      return typeof navigator !== "undefined" && !!navigator.globalPrivacyControl;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("privacyOptOut");
      if (stored === "true") setOptOut(true);
    } catch (err) {
      console.warn("localStorage not accessible:", err);
    }
  }, []);

  const applyConsent = (next) => {
    try {
      localStorage.setItem("privacyOptOut", next ? "true" : "false");
      // simple cookie for other scripts to read
      document.cookie = `aw_consent=${next ? "optout" : "ok"}; path=/; max-age=${60 * 60 * 24 * 365}`;
      setStatusMsg(next ? "Opt-out saved for this browser." : "You are opted in for this browser.");
    } catch (err) {
      console.warn("Unable to persist consent:", err);
      setStatusMsg("Could not save preference in this browser.");
    }
    setTimeout(() => setStatusMsg(""), 2500);
  };

  const onToggle = (e) => {
    const next = e.target.checked;
    setOptOut(next);
    applyConsent(next);
  };

  // ---- Formal Request Form (stub wiring) ----
  const [form, setForm] = useState({ name: "", email: "", phone: "", state: "" });
  const [sending, setSending] = useState(false);
  const [formMsg, setFormMsg] = useState("");

  const submitRequest = async (e) => {
    e.preventDefault();
    setSending(true);
    setFormMsg("");

    try {
      // TODO: replace with your backend endpoint
      // await axios.post("/api/privacy/optout", form);
      await new Promise((r) => setTimeout(r, 700)); // demo delay
      setFormMsg("✅ Your request has been received. We’ll follow up for verification if needed.");
      setForm({ name: "", email: "", phone: "", state: "" });
    } catch (err) {
      console.warn("Opt-out request failed:", err);
      setFormMsg("❌ We couldn’t submit your request. Please email info@allwellcompanionhomecare.com or call (630) 339-9000.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <header className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <h1 className="text-4xl font-bold">Do Not Sell or Share My Personal Information</h1>
          <p className="mt-2 text-white/90 max-w-3xl">
            Manage your preferences for activities that may be considered a “sale” or “sharing”
            of personal information under state privacy laws (e.g., CPRA).
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-5">
            <span className="inline-flex items-center rounded-full bg-white/15 px-3 py-1 text-sm">
              📅 Effective: {effectiveDate}
            </span>
            <span className="inline-flex items-center rounded-full bg-white/15 px-3 py-1 text-sm">
              🛠 Last Updated: {lastUpdated}
            </span>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-6xl mx-auto px-6 py-10">
        <article className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left: Summary & Online Opt-out */}
          <section className="lg:col-span-7 space-y-6">
            <div className="rounded-2xl border bg-white shadow-sm p-6 md:p-8">
              <h2 className="text-2xl font-bold mb-3">Your Privacy Choices</h2>
              <p className="text-gray-700">
                AllWell Companion Home Care LLC does <strong>not</strong> sell personal information
                for money. Some website technologies (like analytics or ads) could be deemed a
                “sale” or “sharing” under certain privacy laws. You can opt out below.
              </p>

              <div className="mt-6 flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-gray-800">Online Opt-Out (this browser)</div>
                  <p className="text-sm text-gray-600">
                    Applies to this device and browser. You can change this any time in Cookie Settings.
                  </p>
                </div>

                {/* Toggle */}
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={optOut}
                    onChange={onToggle}
                    aria-label="Opt out of sale/sharing"
                  />
                  <span className="w-12 h-7 bg-gray-300 rounded-full peer-checked:bg-gray-900 transition-colors"></span>
                  <span className="absolute w-6 h-6 ml-1 rounded-full bg-white shadow transform transition peer-checked:translate-x-5"></span>
                </label>
              </div>

              {statusMsg && (
                <div className="mt-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                  {statusMsg}
                </div>
              )}

              <div className="mt-6 text-sm text-gray-700">
                <strong>Global Privacy Control (GPC):</strong>{" "}
                {gpc ? (
                  <span className="text-emerald-700 font-semibold">Detected — treated as an opt-out.</span>
                ) : (
                  <span>Not detected. You may enable GPC in your browser extensions or settings.</span>
                )}
              </div>
            </div>

            {/* What it covers */}
            <div className="rounded-2xl border bg-white shadow-sm p-6 md:p-8">
              <h3 className="text-xl font-bold mb-3">What this Opt-Out Covers</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Activities that may be deemed a “sale” or “sharing” for cross-context behavioral advertising.</li>
                <li>Online identifiers (e.g., IP address, cookie IDs), browsing activity, device data used for analytics/ads.</li>
                <li>Does not restrict necessary service delivery, security/fraud prevention, or legally permitted disclosures.</li>
              </ul>
            </div>
          </section>

          {/* Right: Formal Request & Help */}
          <aside className="lg:col-span-5 space-y-6">
            <div className="rounded-2xl border bg-white shadow-sm p-6 md:p-8">
              <h3 className="text-xl font-bold mb-3">Submit a Formal Opt-Out Request</h3>
              <p className="text-gray-700">
                Use this form if you want us to process a broader opt-out for your personal information.
                We may contact you to verify your request or your authorized agent’s authority.
              </p>

              <form className="mt-4 space-y-4" onSubmit={submitRequest}>
                <div>
                  <label className="block text-sm font-medium mb-1">Full Name</label>
                  <input
                    type="text"
                    required
                    className="w-full h-11 rounded-md border px-3"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input
                    type="email"
                    required
                    className="w-full h-11 rounded-md border px-3"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone (optional)</label>
                  <input
                    type="tel"
                    className="w-full h-11 rounded-md border px-3"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">State of Residence</label>
                  <input
                    type="text"
                    required
                    className="w-full h-11 rounded-md border px-3"
                    value={form.state}
                    onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                  />
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  className="h-11 w-full rounded-md bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition"
                >
                  {sending ? "Submitting..." : "Submit Request"}
                </button>
              </form>

              {formMsg && (
                <div className="mt-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                  {formMsg}
                </div>
              )}
            </div>

            <div className="rounded-2xl border bg-white shadow-sm p-6 md:p-8">
              <h3 className="text-xl font-bold mb-3">Other Ways to Exercise Your Rights</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Email: <a className="text-emerald-700 underline" href="mailto:info@allwellcompanionhomecare.com">info@allwellcompanionhomecare.com</a></li>
                <li>Phone: <a className="text-emerald-700 underline" href="tel:16303399000">(630) 339-9000</a></li>
                <li>Cookie Settings: use the link in our site footer to reopen the banner and change your choice.</li>
              </ul>
              <div className="text-sm text-gray-600 mt-3">
                We will not discriminate against you for exercising your privacy rights. We do not knowingly
                sell or share personal information of consumers under 16 years of age.
              </div>
            </div>
          </aside>
        </article>

        <div className="text-sm text-gray-500 mt-8">
          © {new Date().getFullYear()} AllWell Companion Home Care LLC. All rights reserved.
        </div>
      </main>
    </div>
  );
}
